/**
 * Éditeur du contenu d'un BROUILLON de gamme : paliers, seuils, lignes de pièces, prix.
 *
 * Tout se règle à l'écran puis s'enregistre d'un bloc (« Enregistrer ») : jamais de modification
 * ligne par ligne côté serveur. La logique — lire un nombre à la française, valider, rang = position,
 * retirer les prix quand on n'a pas le droit — vit dans `domaine/gammes-edition.ts`, où elle se teste ;
 * ce composant ne fait que la montrer.
 *
 * Les messages (points à corriger, refus de l'API) s'affichent dans la barre collée en bas, là où
 * l'on vient de cliquer « Enregistrer » : jamais hors de l'écran.
 *
 * Le travail non enregistré est gardé : fermer ou recharger l'onglet demande confirmation
 * (`beforeunload`), et le menu consulte `garde-modifications` avant de quitter.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  ApiError,
  api,
  type DesignationConnue,
  type EquivalentConnu,
  type GammeDetail,
  type GammeVersion,
  type GenreLigneGamme,
  type Party,
  type PrixConnu,
  type ReferenceDuTarif,
} from '../api/client';
import type { DroitsGammes } from '../domaine/droits';
import {
  ajouterLigne,
  ajouterPalier,
  ajouterSeuil,
  choisirReferenceTarif,
  compterContenu,
  decrirePalier,
  deplacerLigne,
  deplacerPalier,
  designationRemplacable,
  designationsAProposer,
  dupliquerLigne,
  estModifie,
  infobulleDeLaSource,
  majLigne,
  majPalier,
  origineDuPrix,
  prixAProposer,
  prixEnEcart,
  remplirDesignationSiVide,
  retirerLigne,
  retirerPalier,
  retirerSeuil,
  traduireErreurApi,
  utiliserEquivalent,
  utiliserPrix,
  validerContenu,
  versContenuEnvoye,
  versEditable,
  type ChampPrix,
  type ContenuEditable,
  type DefautContenu,
  type LigneEditable,
  type PalierEditable,
  type SeuilEditable,
} from '../domaine/gammes-edition';
import { GENRE_LIGNE, prix as formaterPrix, resumerEcheances } from '../domaine/gammes';
import { signalerModifications } from '../domaine/garde-modifications';
import { ChampReference } from './ChampReference';
import { ErreurFormulaire } from './Formulaire';

const GENRES: readonly GenreLigneGamme[] = ['REPLACE', 'CHECK', 'CONDITIONAL'];

/**
 * Les prix déjà connus d'une référence, avec CE POUR QUOI ils ont été cherchés : la réponse arrive
 * parfois après que l'utilisateur a changé la référence, et un prix proposé pour l'ancienne ne doit
 * jamais s'afficher sous la nouvelle.
 */
interface PrixCherches {
  readonly pour: string;
  readonly liste: readonly PrixConnu[];
}

/** Les désignations déjà connues d'une référence, avec la référence pour laquelle on les a cherchées. */
interface DesignationsCherchees {
  readonly pour: string;
  readonly liste: readonly DesignationConnue[];
}

/** Ce que l'on a cherché pour la référence officielle d'une ligne : la référence elle-même. */
const clePrixPublic = (l: LigneEditable): string => l.reference.trim();
/** … et pour son équivalent : l'équivalent ET son fournisseur (le même texte chez un autre n'est pas la même pièce). */
const clePrixInterne = (l: LigneEditable): string => `${l.equivalent.trim()}|${l.fournisseurId ?? ''}`;

export function EditeurGamme({
  gamme,
  version,
  droits,
  onEnregistre,
  onFermer,
  onConflit,
}: {
  gamme: GammeDetail;
  /** Le brouillon à modifier. L'éditeur est remonté à chaque nouvelle révision : il repart de ce contenu. */
  version: GammeVersion;
  droits: DroitsGammes;
  /** Le contenu est enregistré : le parent relit la gamme et annonce le résultat. */
  onEnregistre: (version: GammeVersion, compte: { paliers: number; lignes: number }) => void;
  onFermer: () => void;
  /** La version a été modifiée par quelqu'un d'autre : relire la gamme (le travail non enregistré est perdu). */
  onConflit: () => void;
}) {
  const origine = useMemo(() => versEditable(version.contenu), [version]);
  const [courant, setCourant] = useState<ContenuEditable>(origine);
  const [fournisseurs, setFournisseurs] = useState<Party[]>([]);
  const [propositions, setPropositions] = useState<Record<string, readonly EquivalentConnu[]>>({});
  const [designationsConnues, setDesignationsConnues] = useState<Record<string, DesignationsCherchees>>({});
  const [prixPublicConnus, setPrixPublicConnus] = useState<Record<string, PrixCherches>>({});
  const [prixInterneConnus, setPrixInterneConnus] = useState<Record<string, PrixCherches>>({});
  // Le dernier texte posé sur une ligne par un choix dans la liste des références (une aide, pas une saisie).
  const textesChoisis = useRef<Record<string, string>>({});
  const [defauts, setDefauts] = useState<DefautContenu[]>([]);
  const [erreurApi, setErreurApi] = useState<ApiError | Error | null>(null);
  const [conflit, setConflit] = useState(false);
  const [enCours, setEnCours] = useState(false);

  const avecPrix = droits.peutEcrirePrix;
  const modifie = estModifie(origine, courant);

  useEffect(() => {
    void api.parties(undefined, 'SUPPLIER').then(setFournisseurs).catch(() => undefined);
  }, []);

  // Du travail non enregistré : le menu et la fermeture de l'onglet doivent le savoir.
  useEffect(() => {
    signalerModifications(modifie);
    return () => signalerModifications(false);
  }, [modifie]);

  useEffect(() => {
    if (!modifie) return;
    const avantFermeture = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', avantFermeture);
    return () => window.removeEventListener('beforeunload', avantFermeture);
  }, [modifie]);

  // Les points à corriger : on amène le premier à l'écran.
  useEffect(() => {
    const premier = defauts[0];
    if (!premier || premier.palier === null) return;
    const palier = courant.paliers[premier.palier];
    if (palier) document.getElementById(`editeur-palier-${palier.cle}`)?.scrollIntoView({ block: 'center' });
    // Seule une nouvelle liste de défauts déclenche le défilement, pas chaque frappe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defauts]);

  const modifier = (fn: (c: ContenuEditable) => ContenuEditable) => {
    setCourant(fn);
    setDefauts([]);
    setErreurApi(null);
  };

  const nomFournisseur = (id: string): string =>
    fournisseurs.find((f) => f.id === id)?.legalName ?? 'Fournisseur inconnu';

  /** Cherche les équivalents DÉJÀ connus de la référence : proposés, jamais posés d'office. */
  const chercherEquivalents = async (ligne: LigneEditable) => {
    const reference = ligne.reference.trim();
    if (reference === '' || ligne.genre === 'CHECK' || ligne.equivalent.trim() !== '') {
      setPropositions((p) => ({ ...p, [ligne.cle]: [] }));
      return;
    }
    try {
      const connus = await api.maintenanceEquivalents(gamme.id, reference);
      setPropositions((p) => ({ ...p, [ligne.cle]: connus }));
    } catch {
      // Une aide : si elle échoue, la saisie manuelle reste entière.
    }
  };

  /**
   * « Tout part de la référence » : cherche la désignation DÉJÀ connue de la référence. Une case vide se
   * remplit d'elle-même (la plus récente), le texte reste modifiable ; une case déjà remplie n'est jamais
   * touchée. Ouvert à tout rôle qui lit les gammes (aucun prix), contrairement aux prix.
   */
  const chercherDesignation = (ligne: LigneEditable) => {
    const reference = ligne.reference.trim();
    if (reference === '' || ligne.genre === 'CHECK') return;
    // Déjà cherché pour cette même référence : on ne redemande pas.
    if (designationsConnues[ligne.cle]?.pour === reference) return;
    void api
      .maintenanceDesignations(gamme.id, reference)
      .then((liste) => {
        setDesignationsConnues((p) => ({ ...p, [ligne.cle]: { pour: reference, liste } }));
        const premiere = liste[0];
        if (premiere) {
          setCourant((c) => remplirDesignationSiVide(c, ligne.cle, reference, premiere.designation));
          setDefauts([]);
        }
      })
      .catch(() => undefined); // Une aide : si elle échoue, la saisie manuelle reste entière.
  };

  /**
   * Cherche les prix DÉJÀ connus de la référence (prix public) et de l'équivalent (prix interne) :
   * proposés sous les cases de prix, jamais posés d'office. Seul qui peut écrire les prix voit ces
   * cases, et seul lui est autorisé à les demander (l'API répond 403 aux autres). `surcharge` sert quand
   * la ligne vient d'être modifiée et que l'état ne l'a pas encore rendue (équivalent choisi en un clic).
   */
  const chercherPrix = (
    ligne: LigneEditable,
    surcharge: { equivalent?: string; fournisseurId?: string | null } = {},
  ) => {
    if (!avecPrix || ligne.genre === 'CHECK') return;
    const vue: LigneEditable = {
      ...ligne,
      equivalent: surcharge.equivalent ?? ligne.equivalent,
      fournisseurId: surcharge.fournisseurId === undefined ? ligne.fournisseurId : surcharge.fournisseurId,
    };

    // Déjà cherché pour cette même référence (ou ce même équivalent chez ce même fournisseur) : on ne redemande pas.
    const reference = vue.reference.trim();
    if (reference !== '' && prixPublicConnus[ligne.cle]?.pour !== clePrixPublic(vue)) {
      const pour = clePrixPublic(vue);
      void api
        .maintenancePrixConnus(gamme.id, reference, 'OFFICIAL')
        .then((liste) => setPrixPublicConnus((p) => ({ ...p, [ligne.cle]: { pour, liste } })))
        .catch(() => undefined); // Une aide : si elle échoue, la saisie manuelle reste entière.
    }

    const equivalent = vue.equivalent.trim();
    if (equivalent !== '' && prixInterneConnus[ligne.cle]?.pour !== clePrixInterne(vue)) {
      const pour = clePrixInterne(vue);
      void api
        .maintenancePrixConnus(gamme.id, equivalent, 'ADAPTABLE', vue.fournisseurId)
        .then((liste) => setPrixInterneConnus((p) => ({ ...p, [ligne.cle]: { pour, liste } })))
        .catch(() => undefined);
    }
  };

  const changerGenre = (ip: number, il: number, genre: GenreLigneGamme) => {
    const ligne = courant.paliers[ip]?.lignes[il];
    if (!ligne || ligne.genre === genre) return;
    if (genre === 'CHECK' && (ligne.reference.trim() !== '' || ligne.equivalent.trim() !== '')) {
      const ok = window.confirm(
        'Un contrôle n’a pas de pièce : sa référence et son équivalent seront effacés. Continuer ?',
      );
      if (!ok) return;
      modifier((c) =>
        majLigne(c, ip, il, (l) => ({ ...l, genre, reference: '', equivalent: '', fournisseurId: null })),
      );
      return;
    }
    modifier((c) => majLigne(c, ip, il, (l) => ({ ...l, genre })));
  };

  const enregistrer = async () => {
    const trouves = validerContenu(courant, { avecPrix });
    if (trouves.length > 0) {
      setDefauts(trouves);
      setErreurApi(null);
      return;
    }

    setEnCours(true);
    setErreurApi(null);
    setConflit(false);
    try {
      const rendue = await api.replaceMaintenanceContent(gamme.id, version.id, {
        contenu: versContenuEnvoye(courant, { avecPrix }),
        revision: version.revision,
      });
      onEnregistre(rendue, compterContenu(courant));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setConflit(true);
      } else if (e instanceof ApiError) {
        setErreurApi(new ApiError(e.status, traduireErreurApi(e.message, courant), e.details?.map((d) => traduireErreurApi(d, courant))));
      } else {
        setErreurApi(e instanceof Error ? e : new Error('Enregistrement impossible.'));
      }
    } finally {
      setEnCours(false);
    }
  };

  const fermer = () => {
    if (modifie && !window.confirm('Abandonner vos modifications non enregistrées ?')) return;
    onFermer();
  };

  const lignesEnDefaut = new Set(
    defauts.filter((d) => d.palier !== null && d.ligne !== null).map((d) => `${d.palier}:${d.ligne}`),
  );
  const paliersEnDefaut = new Set(defauts.filter((d) => d.ligne === null && d.palier !== null).map((d) => d.palier));

  const { heures, mois } = version.echeances;
  const nbPaliers = courant.paliers.length;

  return (
    <div className="editeur-gamme">
      <div className="message info">
        Vous modifiez le <strong>brouillon de la version {version.versionNumber}</strong>. Rien ne s’applique aux
        machines tant que vous ne l’avez pas publié. <strong>Enregistrez</strong> pour garder votre travail.
        Les paliers vont du <strong>moins complet au plus complet</strong> : à un même point d’échéance, seul le
        <strong> dernier palier de la liste</strong> qui tombe à cet instant s’applique, et il porte donc la liste
        complète de ses pièces.
      </div>

      {(heures.length > 0 || mois.length > 0) && (
        <details className="echeances-theoriques">
          <summary>Échéances théoriques (dernier enregistrement)</summary>
          {heures.length > 0 && (
            <p className="aide">
              <strong>Au compteur :</strong> {resumerEcheances(heures, 'h')}
            </p>
          )}
          {mois.length > 0 && (
            <p className="aide">
              <strong>Au calendrier :</strong> {resumerEcheances(mois, 'mois')}
            </p>
          )}
        </details>
      )}

      {courant.paliers.length === 0 && (
        <div className="vide">Ce brouillon n’a encore aucun palier. Ajoutez-en un pour commencer.</div>
      )}

      {courant.paliers.map((palier, ip) => (
        <EditeurPalier
          key={palier.cle}
          gammeId={gamme.id}
          palier={palier}
          position={ip}
          total={nbPaliers}
          avecPrix={avecPrix}
          fournisseurs={fournisseurs}
          nomFournisseur={nomFournisseur}
          propositions={propositions}
          designationsConnues={designationsConnues}
          prixPublicConnus={prixPublicConnus}
          prixInterneConnus={prixInterneConnus}
          lignesEnDefaut={lignesEnDefaut}
          enDefaut={paliersEnDefaut.has(ip)}
          desactive={enCours}
          onPalier={(fn) => modifier((c) => majPalier(c, ip, fn))}
          onMonter={() => modifier((c) => deplacerPalier(c, ip, -1))}
          onDescendre={() => modifier((c) => deplacerPalier(c, ip, 1))}
          onRetirer={() => {
            const n = palier.lignes.length;
            const texte =
              n > 0
                ? `Retirer le palier ${palier.code || ip + 1} et ses ${n} ligne${n > 1 ? 's' : ''} ?`
                : `Retirer le palier ${palier.code || ip + 1} ?`;
            if (window.confirm(`${texte}\n\nCe n’est définitif qu’une fois enregistré.`)) {
              modifier((c) => retirerPalier(c, ip));
            }
          }}
          onAjouterLigne={() => modifier((c) => ajouterLigne(c, ip))}
          onLigne={(il, fn) => modifier((c) => majLigne(c, ip, il, fn))}
          onGenre={(il, genre) => changerGenre(ip, il, genre)}
          onMonterLigne={(il) => modifier((c) => deplacerLigne(c, ip, il, -1))}
          onDescendreLigne={(il) => modifier((c) => deplacerLigne(c, ip, il, 1))}
          onDupliquerLigne={(il) => modifier((c) => dupliquerLigne(c, ip, il))}
          onRetirerLigne={(il) => modifier((c) => retirerLigne(c, ip, il))}
          onAjouterSeuil={() => modifier((c) => ajouterSeuil(c, ip))}
          onRetirerSeuil={(is) => modifier((c) => retirerSeuil(c, ip, is))}
          onChercherEquivalents={(ligne) => void chercherEquivalents(ligne)}
          onChercherDesignation={chercherDesignation}
          onChercherPrix={chercherPrix}
          onChoisirReference={(il, choix) => {
            const ligne = palier.lignes[il];
            if (!ligne) return;
            // Les textes que les AIDES ont posés sur cette ligne (désignations connues de la référence qu'on
            // quitte, et le dernier texte posé par un choix dans la liste) : ils se remplacent par ceux de la
            // nouvelle référence, un texte écrit à la main jamais.
            const dernierChoix = textesChoisis.current[ligne.cle];
            const textesDAide = [
              ...(designationsConnues[ligne.cle]?.liste.map((d) => d.designation) ?? []),
              ...(dernierChoix === undefined ? [] : [dernierChoix]),
            ];
            const remplace = designationRemplacable(ligne.designation, textesDAide);
            modifier((c) => choisirReferenceTarif(c, ip, il, choix, textesDAide));
            if (remplace) textesChoisis.current[ligne.cle] = choix.description;
            // La ligne telle qu'elle sera une fois la référence posée (l'état n'est pas encore rendu) :
            // la désignation, le prix et l'équivalent connus se proposent aussitôt, comme en quittant la case.
            const posee: LigneEditable = {
              ...ligne,
              reference: choix.reference,
              designation: remplace ? choix.description : ligne.designation,
            };
            void chercherEquivalents(posee);
            chercherDesignation(posee);
            chercherPrix(posee);
          }}
          onUtiliserPrix={(il, champ, montant) => modifier((c) => utiliserPrix(c, ip, il, champ, montant))}
          onUtiliserEquivalent={(il, eq) => {
            modifier((c) => utiliserEquivalent(c, ip, il, eq));
            const ligne = palier.lignes[il];
            if (!ligne) return;
            setPropositions((p) => ({ ...p, [ligne.cle]: [] }));
            // L'équivalent vient d'être choisi : son prix connu se propose aussitôt, sans attendre un clic ailleurs.
            chercherPrix(ligne, { equivalent: eq.reference, fournisseurId: eq.supplierPartyId });
          }}
        />
      ))}

      <button
        type="button"
        className="secondaire"
        disabled={enCours}
        onClick={() => modifier((c) => ajouterPalier(c))}
      >
        + Ajouter un palier
      </button>

      <div className="editeur-barre">
        {defauts.length > 0 && (
          <div className="message erreur">
            Corrigez {defauts.length} point{defauts.length > 1 ? 's' : ''} avant d’enregistrer :
            <ul className="details-erreur">
              {defauts.slice(0, 8).map((d, i) => (
                <li key={`${d.message}-${i}`}>{d.message}</li>
              ))}
              {defauts.length > 8 && <li>… et {defauts.length - 8} autre{defauts.length - 8 > 1 ? 's' : ''}.</li>}
            </ul>
          </div>
        )}
        <ErreurFormulaire message={erreurApi?.message ?? null} details={erreurApi instanceof ApiError ? erreurApi.details : undefined} />
        {conflit && (
          <div className="message erreur">
            Ce brouillon a été modifié par quelqu’un d’autre depuis votre ouverture : rechargez-le avant
            d’enregistrer, sinon vous écraseriez son travail.{' '}
            <button type="button" className="secondaire" onClick={onConflit}>
              Recharger la version (vos modifications non enregistrées seront perdues)
            </button>
          </div>
        )}
        <div className="editeur-barre-actions">
          <span className={modifie ? 'etat attente' : 'aide'}>
            {modifie ? 'Modifications non enregistrées' : 'Aucune modification'}
          </span>
          <div className="saisie-ligne">
            <button type="button" className="secondaire" disabled={enCours} onClick={fermer}>
              {modifie ? 'Abandonner les modifications' : 'Fermer l’éditeur'}
            </button>
            <button type="button" disabled={enCours || !modifie} onClick={() => void enregistrer()}>
              {enCours ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Sous une case de prix : les prix DÉJÀ connus de la référence, à reprendre en un clic quand la case
 * est vide ; quand elle est remplie, un simple rappel si le prix saisi s'écarte beaucoup du prix
 * habituel. Jamais posé d'office, jamais un refus.
 */
function PrixProposes({
  saisi,
  connus,
  desactive,
  onUtiliser,
}: {
  saisi: string;
  connus: readonly PrixConnu[];
  desactive: boolean;
  onUtiliser: (montant: number) => void;
}) {
  const proposes = prixAProposer(saisi, connus);
  const ecart = proposes.length === 0 ? prixEnEcart(saisi, connus) : null;

  return (
    <>
      {proposes.map((p) => (
        <div key={p.price} className="editeur-prix-propose">
          <button
            type="button"
            className="secondaire discret editeur-proposition"
            disabled={desactive}
            title={infobulleDeLaSource('Prix', p)}
            onClick={() => onUtiliser(p.price)}
          >
            Utiliser {formaterPrix(p.price)}
          </button>
          <span className="aide">{origineDuPrix(p)}</span>
        </div>
      ))}
      {ecart && (
        <div
          className="editeur-prix-ecart"
          role="note"
          title={infobulleDeLaSource('Prix', ecart)}
        >
          Prix habituel : <strong>{formaterPrix(ecart.price)}</strong>
          <button
            type="button"
            className="secondaire discret editeur-proposition"
            disabled={desactive}
            onClick={() => onUtiliser(ecart.price)}
          >
            Utiliser {formaterPrix(ecart.price)}
          </button>
        </div>
      )}
    </>
  );
}

function EditeurPalier({
  gammeId,
  palier,
  position,
  total,
  avecPrix,
  fournisseurs,
  nomFournisseur,
  propositions,
  designationsConnues,
  prixPublicConnus,
  prixInterneConnus,
  lignesEnDefaut,
  enDefaut,
  desactive,
  onPalier,
  onMonter,
  onDescendre,
  onRetirer,
  onAjouterLigne,
  onLigne,
  onGenre,
  onMonterLigne,
  onDescendreLigne,
  onDupliquerLigne,
  onRetirerLigne,
  onAjouterSeuil,
  onRetirerSeuil,
  onChercherEquivalents,
  onChercherDesignation,
  onChercherPrix,
  onChoisirReference,
  onUtiliserPrix,
  onUtiliserEquivalent,
}: {
  /** La gamme dont on édite le brouillon : la liste de références est celle du tarif de SON constructeur. */
  gammeId: string;
  palier: PalierEditable;
  position: number;
  total: number;
  avecPrix: boolean;
  fournisseurs: readonly Party[];
  nomFournisseur: (id: string) => string;
  propositions: Record<string, readonly EquivalentConnu[]>;
  designationsConnues: Record<string, DesignationsCherchees>;
  prixPublicConnus: Record<string, PrixCherches>;
  prixInterneConnus: Record<string, PrixCherches>;
  lignesEnDefaut: ReadonlySet<string>;
  enDefaut: boolean;
  desactive: boolean;
  onPalier: (fn: (p: PalierEditable) => PalierEditable) => void;
  onMonter: () => void;
  onDescendre: () => void;
  onRetirer: () => void;
  onAjouterLigne: () => void;
  onLigne: (il: number, fn: (l: LigneEditable) => LigneEditable) => void;
  onGenre: (il: number, genre: GenreLigneGamme) => void;
  onMonterLigne: (il: number) => void;
  onDescendreLigne: (il: number) => void;
  onDupliquerLigne: (il: number) => void;
  onRetirerLigne: (il: number) => void;
  onAjouterSeuil: () => void;
  onRetirerSeuil: (is: number) => void;
  onChercherEquivalents: (ligne: LigneEditable) => void;
  onChercherDesignation: (ligne: LigneEditable) => void;
  onChercherPrix: (ligne: LigneEditable, surcharge?: { equivalent?: string; fournisseurId?: string | null }) => void;
  /** Une référence a été choisie dans la liste du tarif : le parent la pose et lance les aides. */
  onChoisirReference: (il: number, choix: ReferenceDuTarif) => void;
  onUtiliserPrix: (il: number, champ: ChampPrix, montant: number) => void;
  onUtiliserEquivalent: (il: number, eq: { reference: string; supplierPartyId: string }) => void;
}) {
  const majSeuil = (is: number, patch: Partial<SeuilEditable>) =>
    onPalier((p) => ({ ...p, seuils: p.seuils.map((s, i) => (i === is ? { ...s, ...patch } : s)) }));

  const phrase = decrirePalier(palier);
  const dimensionsPrises = new Set(palier.seuils.map((s) => s.dimension));

  return (
    <section id={`editeur-palier-${palier.cle}`} className={`editeur-palier${enDefaut ? ' en-erreur' : ''}`}>
      <header className="editeur-palier-entete">
        <input
          type="text"
          className="editeur-code"
          aria-label="Code du palier"
          placeholder="A"
          value={palier.code}
          disabled={desactive}
          onChange={(e) => onPalier((p) => ({ ...p, code: e.target.value }))}
        />
        <input
          type="text"
          aria-label="Libellé du palier"
          placeholder="Libellé, par exemple « 6 000 h »"
          value={palier.libelle}
          disabled={desactive}
          onChange={(e) => onPalier((p) => ({ ...p, libelle: e.target.value }))}
        />
        <input
          type="text"
          inputMode="decimal"
          className="editeur-temps-mo"
          aria-label="Temps de main d’œuvre prévu, en heures, par technicien"
          title="Temps de main d’œuvre prévu pour ce palier, en heures, par technicien : il préremplit les heures prévues d’une intervention qui choisit ce palier"
          placeholder="Main d’œuvre (h)"
          value={palier.tempsMo}
          disabled={desactive}
          onChange={(e) => onPalier((p) => ({ ...p, tempsMo: e.target.value }))}
        />
        <div className="saisie-ligne">
          <button type="button" className="secondaire discret" title="Rendre ce palier moins complet" aria-label="Monter le palier" disabled={desactive || position === 0} onClick={onMonter}>
            ↑
          </button>
          <button type="button" className="secondaire discret" title="Rendre ce palier plus complet" aria-label="Descendre le palier" disabled={desactive || position === total - 1} onClick={onDescendre}>
            ↓
          </button>
          <button type="button" className="secondaire discret danger-discret" disabled={desactive} onClick={onRetirer}>
            Retirer le palier
          </button>
        </div>
      </header>

      <p className="aide">
        {phrase ?? 'Complétez les seuils pour voir à quelle fréquence ce palier se déclenche.'}
        {' · '}
        {position === total - 1 && total > 1 ? 'palier le plus complet' : `rang ${position + 1} sur ${total}`}
      </p>

      <div className="editeur-seuils">
        {palier.seuils.map((s, is) => (
          <div className="saisie-ligne" key={is}>
            <select
              aria-label="Type de seuil"
              value={s.dimension}
              disabled={desactive}
              onChange={(e) => majSeuil(is, { dimension: e.target.value === 'MONTHS' ? 'MONTHS' : 'HOURS' })}
            >
              <option value="HOURS" disabled={s.dimension !== 'HOURS' && dimensionsPrises.has('HOURS')}>
                Heures
              </option>
              <option value="MONTHS" disabled={s.dimension !== 'MONTHS' && dimensionsPrises.has('MONTHS')}>
                Mois
              </option>
            </select>
            <label className="editeur-seuil-champ">
              premier à
              <input
                type="text"
                inputMode="decimal"
                className="champ-numerique"
                aria-label="Premier seuil"
                value={s.premier}
                disabled={desactive}
                onChange={(e) => majSeuil(is, { premier: e.target.value })}
              />
            </label>
            {!s.uneSeuleFois && (
              <label className="editeur-seuil-champ">
                puis tous les
                <input
                  type="text"
                  inputMode="decimal"
                  className="champ-numerique"
                  aria-label="Intervalle"
                  value={s.intervalle}
                  disabled={desactive}
                  onChange={(e) => majSeuil(is, { intervalle: e.target.value })}
                />
              </label>
            )}
            <label className="case">
              <input
                type="checkbox"
                checked={s.uneSeuleFois}
                disabled={desactive}
                onChange={(e) => majSeuil(is, { uneSeuleFois: e.target.checked })}
              />
              une seule fois
            </label>
            {palier.seuils.length > 1 && (
              <button type="button" className="secondaire discret" aria-label="Retirer ce seuil" disabled={desactive} onClick={() => onRetirerSeuil(is)}>
                ✕
              </button>
            )}
          </div>
        ))}
        {palier.seuils.length < 2 && (
          <button type="button" className="secondaire discret" disabled={desactive} onClick={onAjouterSeuil}>
            + Ajouter un seuil ({dimensionsPrises.has('HOURS') ? 'mois' : 'heures'})
          </button>
        )}
      </div>

      {palier.lignes.length === 0 ? (
        <div className="aide">Aucune ligne pour ce palier.</div>
      ) : (
        <div className="tableau-defilant">
          <table className="editeur-table">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Genre</th>
                {/* La désignation prend toute la place que les autres colonnes lui laissent, 300 px au moins. */}
                <th style={{ minWidth: 300 }}>Désignation</th>
                <th style={{ width: 170 }}>Référence constructeur</th>
                <th style={{ width: 70 }}>Qté</th>
                <th style={{ width: 60 }}>Unité</th>
                <th style={{ width: 170 }}>Équivalent</th>
                {avecPrix && <th style={{ width: 110 }}>Prix public</th>}
                {avecPrix && <th style={{ width: 110 }}>Prix interne</th>}
                <th style={{ width: 130 }} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {palier.lignes.map((l, il) => {
                const controle = l.genre === 'CHECK';
                const proposees = propositions[l.cle] ?? [];
                const connu = l.fournisseurId === null || fournisseurs.some((f) => f.id === l.fournisseurId);
                // Un prix proposé pour une AUTRE référence que celle de la ligne (réponse tardive) ne s'affiche pas.
                const cherchePublic = prixPublicConnus[l.cle];
                const chercheInterne = prixInterneConnus[l.cle];
                const chercheTexte = designationsConnues[l.cle];
                const connuesTexte = chercheTexte && chercheTexte.pour === l.reference.trim() ? chercheTexte.liste : [];
                const connusPublic = cherchePublic && cherchePublic.pour === clePrixPublic(l) ? cherchePublic.liste : [];
                const connusInterne = chercheInterne && chercheInterne.pour === clePrixInterne(l) ? chercheInterne.liste : [];
                return (
                  <tr key={l.cle} className={lignesEnDefaut.has(`${position}:${il}`) ? 'ligne-en-erreur' : undefined}>
                    <td>
                      <select
                        aria-label="Genre de la ligne"
                        value={l.genre}
                        disabled={desactive}
                        onChange={(e) => onGenre(il, GENRES.find((g) => g === e.target.value) ?? 'REPLACE')}
                      >
                        {GENRES.map((g) => (
                          <option key={g} value={g}>
                            {GENRE_LIGNE[g].texte}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        aria-label="Désignation"
                        placeholder="Désignation"
                        value={l.designation}
                        disabled={desactive}
                        onChange={(e) => onLigne(il, (x) => ({ ...x, designation: e.target.value }))}
                      />
                      {designationsAProposer(l.designation, connuesTexte).map((d) => (
                        <button
                          key={d.designation}
                          type="button"
                          className="secondaire discret editeur-proposition"
                          disabled={desactive}
                          title={infobulleDeLaSource('Désignation', d)}
                          onClick={() => onLigne(il, (x) => ({ ...x, designation: d.designation }))}
                        >
                          Utiliser « {d.designation} »
                        </button>
                      ))}
                      <input
                        type="text"
                        className="editeur-note"
                        aria-label="Note"
                        placeholder="Note (facultatif)"
                        value={l.note}
                        disabled={desactive}
                        onChange={(e) => onLigne(il, (x) => ({ ...x, note: e.target.value }))}
                      />
                    </td>
                    <td>
                      <ChampReference
                        gammeId={gammeId}
                        valeur={l.reference}
                        placeholder={controle ? 'Aucune (contrôle)' : 'Référence'}
                        desactive={desactive || controle}
                        onChange={(reference) => onLigne(il, (x) => ({ ...x, reference }))}
                        onChoisir={(choix) => onChoisirReference(il, choix)}
                        onQuitte={() => {
                          onChercherEquivalents(l);
                          onChercherDesignation(l);
                          onChercherPrix(l);
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="champ-numerique"
                        aria-label="Quantité"
                        placeholder="inconnue"
                        title="Laissée vide, la quantité reste inconnue : elle n’est jamais remplacée par 1."
                        value={l.quantite}
                        disabled={desactive}
                        onChange={(e) => onLigne(il, (x) => ({ ...x, quantite: e.target.value }))}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        aria-label="Unité"
                        placeholder="l, m…"
                        value={l.unite}
                        disabled={desactive}
                        onChange={(e) => onLigne(il, (x) => ({ ...x, unite: e.target.value }))}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        aria-label="Référence de l’équivalent"
                        placeholder={controle ? '—' : 'Équivalent'}
                        value={l.equivalent}
                        disabled={desactive || controle}
                        onChange={(e) => onLigne(il, (x) => ({ ...x, equivalent: e.target.value }))}
                        onBlur={() => onChercherPrix(l)}
                      />
                      <select
                        aria-label="Fournisseur de l’équivalent"
                        value={l.fournisseurId ?? ''}
                        disabled={desactive || controle}
                        onChange={(e) => {
                          const choisi = e.target.value === '' ? null : e.target.value;
                          onLigne(il, (x) => ({ ...x, fournisseurId: choisi }));
                          // Le prix connu d'un équivalent dépend de son fournisseur : on le cherche pour le nouveau.
                          onChercherPrix(l, { fournisseurId: choisi });
                        }}
                      >
                        <option value="">Fournisseur…</option>
                        {!connu && l.fournisseurId !== null && <option value={l.fournisseurId}>{nomFournisseur(l.fournisseurId)}</option>}
                        {fournisseurs.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.legalName}
                          </option>
                        ))}
                      </select>
                      {l.equivalent.trim() === '' &&
                        proposees.map((eq) => (
                          <button
                            key={`${eq.supplierPartyId}-${eq.reference}`}
                            type="button"
                            className="secondaire discret editeur-proposition"
                            disabled={desactive}
                            onClick={() => onUtiliserEquivalent(il, eq)}
                          >
                            Utiliser {eq.reference} ({eq.supplierName})
                          </button>
                        ))}
                    </td>
                    {avecPrix && (
                      <td>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="champ-numerique"
                          aria-label="Prix public"
                          placeholder="—"
                          value={l.prixPublic}
                          disabled={desactive}
                          onChange={(e) => onLigne(il, (x) => ({ ...x, prixPublic: e.target.value }))}
                        />
                        <PrixProposes
                          saisi={l.prixPublic}
                          connus={connusPublic}
                          desactive={desactive}
                          onUtiliser={(montant) => onUtiliserPrix(il, 'prixPublic', montant)}
                        />
                      </td>
                    )}
                    {avecPrix && (
                      <td>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="champ-numerique"
                          aria-label="Prix interne"
                          placeholder="—"
                          value={l.prixInterne}
                          disabled={desactive}
                          onChange={(e) => onLigne(il, (x) => ({ ...x, prixInterne: e.target.value }))}
                        />
                        <PrixProposes
                          saisi={l.prixInterne}
                          connus={connusInterne}
                          desactive={desactive}
                          onUtiliser={(montant) => onUtiliserPrix(il, 'prixInterne', montant)}
                        />
                      </td>
                    )}
                    <td className="editeur-actions">
                      <button type="button" className="secondaire discret" title="Monter" aria-label="Monter la ligne" disabled={desactive || il === 0} onClick={() => onMonterLigne(il)}>
                        ↑
                      </button>
                      <button type="button" className="secondaire discret" title="Descendre" aria-label="Descendre la ligne" disabled={desactive || il === palier.lignes.length - 1} onClick={() => onDescendreLigne(il)}>
                        ↓
                      </button>
                      <button type="button" className="secondaire discret" title="Dupliquer sous cette ligne" aria-label="Dupliquer la ligne" disabled={desactive} onClick={() => onDupliquerLigne(il)}>
                        ⧉
                      </button>
                      <button type="button" className="secondaire discret danger-discret" title="Retirer la ligne" aria-label="Retirer la ligne" disabled={desactive} onClick={() => onRetirerLigne(il)}>
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <button type="button" className="secondaire discret" disabled={desactive} onClick={onAjouterLigne}>
        + Ajouter une ligne
      </button>
    </section>
  );
}
